import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import EmojiFrequentIcon from '@atlaskit/icon/core/clock';
import CrossIcon from '@atlaskit/icon/core/cross';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import {
	getRecentQueryObject,
	RECENT_SEARCH_KEY,
	removeRecentSearch,
} from '@atlassian/search-common/recent-searches';

import { SearchLink } from '../../../../../common/ui/search-link';
import {
	type EventWithTarget,
	type SearchDropdownElementProps,
	type SearchLinkProps,
	SearchLinkTypes,
} from '../../../../../common/ui/search-link/types';
import { onDropdownResultClicked } from '../../../../../common/utils/events/dropdown-result-clicked';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../../controllers/analytics';
import { usePrimaryProduct, useUser } from '../../../../../controllers/store/bootstrap';
import {
	useDropdown,
	useDropdownActions,
	useRecentSearchResults,
	useResultCount,
} from '../../../../../controllers/store/dropdown';
import { useSearchActions } from '../../../../../controllers/store/search';

import { messages } from './messages';

const iconStyles = xcss({
	backgroundColor: 'color.background.neutral',
	borderRadius: 'radius.xsmall',
	width: '24px',
	height: '24px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

export const RecentSearchResults = ({
	getElementIndex,
}: SearchDropdownElementProps): React.JSX.Element => {
	const { setSearchQuery } = useSearchActions();
	const [resultCount] = useResultCount();
	const [, { setShowDropdown }] = useDropdown();

	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	const fireAnalytics = useCallback(
		(event: EventWithTarget) => {
			const index = event.target && getElementIndex ? getElementIndex(event.target) : undefined;

			fireAnalyticsEvent(
				onDropdownResultClicked({
					commonAttributes,
					nonPrivacySafeAttributes,
					resultType: SearchLinkTypes.RecentSearches,
					...(index !== undefined ? { index } : {}),
				}),
			);
		},
		[getElementIndex, fireAnalyticsEvent, commonAttributes, nonPrivacySafeAttributes],
	);

	const onClick = (query: string, event: EventWithTarget) => {
		setShowDropdown(false);
		fireAnalytics(event);
		setSearchQuery(query);
	};

	const [recentSearches] = useRecentSearchResults();

	return (
		<>
			{recentSearches.slice(0, resultCount.recentSearch).map((result, index) => (
				<SearchLink
					title={result.title}
					icon={<RecentSearchIcon />}
					onClick={(event) => onClick(result.title, event)}
					key={`${index}-${result.title}`}
					rightPanel={<RecentSearchRightPanel title={result.title} />}
				/>
			))}
		</>
	);
};

export const RecentSearchIcon = (): React.JSX.Element => {
	const intl = useIntl();
	return (
		<Box xcss={iconStyles}>
			<EmojiFrequentIcon
				color="currentColor"
				label={intl.formatMessage(messages.recentSearchIconLabel)}
				spacing="spacious"
			/>
		</Box>
	);
};

const recentSearchRightPanelStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	font: 'font.body.small',
});

const subheadingStyles = xcss({
	marginRight: 'space.150',
});

export const RecentSearchRightPanel = ({ title }: { title: string }): React.JSX.Element => {
	const intl = useIntl();
	const [user] = useUser();
	const [primaryProduct] = usePrimaryProduct();
	const { setRecentSearchResults } = useDropdownActions();

	const onClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			event.stopPropagation();
			if (user?.id && primaryProduct) {
				const newRecentSearches: SearchLinkProps[] = removeRecentSearch({
					value: title,
					userId: user.id,
					productId: primaryProduct,
					key: RECENT_SEARCH_KEY,
				}).map((recentSearchItem) => ({
					// Todo: cleanup this so that we only need to pass the search title.
					title: getRecentQueryObject(recentSearchItem).query,
					icon: <></>,
					onClick: () => null,
				}));
				setRecentSearchResults(newRecentSearches);
			}
		},
		[primaryProduct, title, setRecentSearchResults, user?.id],
	);

	return (
		<Box xcss={recentSearchRightPanelStyles}>
			<Box as="span" xcss={subheadingStyles}>
				{intl.formatMessage(messages.removeRecentSearchSubheading)}
			</Box>
			<IconButton
				icon={(iconProps) => (
					<CrossIcon
						{...iconProps}
						label={intl.formatMessage(messages.removeRecentSearchIcon)}
						color="currentColor"
					/>
				)}
				spacing={'compact'}
				onClick={onClick}
				label={intl.formatMessage(messages.removeRecentSearchIcon)}
				appearance={'subtle'}
			/>
		</Box>
	);
};
